import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { createLogger } from '../../utils/Logger';

import DaltonError, { DaltonErrorResponse } from './DaltonError';
import { getBaseURL, getClientApplicationHeader } from './utils';

const IS_PROD = process.env.NODE_ENV === 'production';

const logger = createLogger('Dalton', '#2B98F0');

const client = axios.create({
    baseURL: getBaseURL(),
    headers: {
        'X-Client-Application': getClientApplicationHeader(),
    },
});

function logError(error: any) {
    if (IS_PROD) {
        return;
    }

    if (error.response) {
        const config: AxiosRequestConfig = error.response.config;
        let requestBody: any;

        try {
            requestBody = JSON.parse(config.data);
        } catch (error) {
            requestBody = config.data;
        }

        logger.log(
            `${String(error.response.config.method).toUpperCase()} ${error.response.config.url} `,
            [`status: %c${error.response.status}`, 'color: red;'],
            ['config:', error.response.config],
            requestBody ? ['request:', requestBody] : undefined,
            [error.response.data]
        );
    } else if (error.request) {
        const config: AxiosRequestConfig = error.config;
        let requestBody: any;

        try {
            requestBody = JSON.parse(config.data);
        } catch (error) {
            requestBody = config.data;
        }

        logger.log(
            `${String(error.config.method).toUpperCase()} ${error.config.url} `,
            ['config:', error.config],
            requestBody ? ['request:', requestBody] : undefined,
            error.request
        );
    }
}

function logResponse(response: AxiosResponse) {
    if (IS_PROD) {
        return;
    }

    let requestBody: any;

    try {
        requestBody = JSON.parse(response.config.data);
    } catch (error) {
        requestBody = response.config.data;
    }

    logger.log(
        `${String(response.config.method).toUpperCase()} ${response.config.url} `,
        [`status: %c${response.status}`, 'color: blue;'],
        ['config:', response.config],
        requestBody ? ['request:', requestBody] : undefined,
        [response.data]
    );
}

client.interceptors.response.use(
    /**
     * Log the request and response details in development
     */
    response => {
        logResponse(response);
        return response;
    },
    /**
     * Parse the Dalton errors
     */
    error => {
        logError(error);

        if (error.response) {
            const data: DaltonErrorResponse = error.response.data;

            const errors = Array.isArray(data.errors)
                ? data.errors.map(err => new DaltonError(err, error.response))
                : [new DaltonError({ field: null, message: error.message }, error.response)];

            return Promise.reject({
                ...data,
                config: error.response.config,
                errors,
                status: error.response.status,
            });
        } else if (error.request) {
            return Promise.reject({
                errors: [
                    new DaltonError({ field: null, message: error.message || error.request.statusText || 'There was an error.' }),
                ],
            });
        }

        return Promise.reject({
            errors: [
                new DaltonError({ field: null, message: error.message }),
            ],
        });
    }
);

export default client;
